import {
  IngestionBrandInfo,
  IngestionJob,
  IngestionJobLog,
  IngestionJobType,
} from '../../services/integrationApi';
import { formatDuration, formatRelativeTime } from './connectorHealth';
import { ingest } from './ingestionTokens';
import { tokens } from '../../theme/tokens';
import { classifyRunOutcome, RunOutcome } from './runOutcome';

export const JOB_TYPE_LABELS: Record<IngestionJobType, string> = {
  full_scrape: 'Full Scrape',
  incremental_scrape: 'Incremental',
  rescrape_missing: 'Re-scrape Missing',
  api_ingestion: 'API Ingestion',
  image_hosting: 'Image Hosting',
  sync_canonical: 'Sync Canonical',
  price_refresh: 'Price Refresh',
  stock_refresh: 'Stock Refresh',
  market_pricing_refresh: 'Market Pricing',
  data_fill: '3P fill (B&H)',
};

export type RunVerdict = 'Completed' | 'Failed' | 'Running' | 'Pending' | 'Cancelled';

/** Only these two say what a run did; the rest are still in flight or abandoned. */
export function isTerminalVerdict(verdict: RunVerdict): boolean {
  return verdict === 'Completed' || verdict === 'Failed';
}

export interface RunVerdictTone {
  bg: string;
  ink: string;
}

export const VERDICT_TONES: Record<RunVerdict, RunVerdictTone> = {
  Completed: { bg: ingest.color.successTint, ink: ingest.color.successInk },
  Failed: { bg: ingest.color.dangerTint, ink: ingest.color.dangerInk },
  Running: { bg: ingest.color.accentTint, ink: ingest.color.accentHover },
  Pending: { bg: tokens.signal.neutral.tint, ink: ingest.color.muted },
  Cancelled: { bg: tokens.signal.neutral.tint, ink: ingest.color.muted },
};

/**
 * One record per run. Every number the console shows about a run — the history
 * row, its expanded log, the Overview "Last run" card, the failure alert — is
 * read off this record, so they can never disagree. Never re-derive a count at
 * the render site.
 */
export interface RunFacts {
  id: string;
  verdict: RunVerdict;
  kind: string;
  trigger: string;
  actor: string;
  processed: number;
  created: number;
  updated: number;
  duration: string;
  startedAt?: string;
  startedLabel: string;
  relative: string;
  /** Row headline: what the run actually did. */
  result: string;
  /** Sub-line: "{date} · {kind} · {trigger} · {duration}". */
  meta: string;
  logs: IngestionJobLog[];
  error?: string;
  /** Warning caption for a completed run with many failed checks ("N of M failed"). */
  partialFailure?: string;
  /** Status plus result: an all-failed run stored as completed reads as failed. */
  outcome: RunOutcome;
  isRunning: boolean;
}

const VERDICTS: Record<string, RunVerdict> = {
  completed: 'Completed',
  failed: 'Failed',
  running: 'Running',
  pending: 'Pending',
  cancelled: 'Cancelled',
};

export function formatRunTimestamp(value?: string): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** First error the run recorded, from its result or its own log tail. */
export function runError(job: IngestionJob): string | undefined {
  if (job.error) return job.error;
  const fromResult = job.result?.errors?.find((entry) => Boolean(entry));
  if (fromResult) return fromResult;
  const fromLogs = [...(job.recentLogs || [])].reverse().find((log) => log.level === 'error');
  return fromLogs?.message;
}

/**
 * Scheduled runs cannot be spotted by a missing email: `scheduledBrandScraper`
 * writes `triggeredByEmail: 'system@motzu.io'`, and the vendor-connector overlay
 * writes 'Schedule' / 'API connector' there. Classify on `triggeredBy`, which is
 * the field the writers actually key, and only fall back to the email for a run
 * a person started.
 */
const SCHEDULE_IDENTITIES = new Set(['scheduled', 'schedule', 'remediation_dispatcher']);

export function resolveTrigger(job: IngestionJob): { trigger: string; actor: string } {
  const by = String(job.triggeredBy || '');

  if (by.startsWith('system:') || SCHEDULE_IDENTITIES.has(by)) {
    return { trigger: 'Schedule', actor: 'schedule' };
  }
  if (by === 'api_connector') {
    return { trigger: 'Connector', actor: 'API connector' };
  }
  if (job.triggeredByEmail) {
    return { trigger: 'Manual', actor: job.triggeredByEmail };
  }
  return { trigger: 'Schedule', actor: by || 'schedule' };
}

export interface RunFactsOptions {
  /** Catalog size, so an aborted run can read "43 of 91 processed". */
  catalogSize?: number;
}

export function runFacts(job: IngestionJob, options: RunFactsOptions = {}): RunFacts {
  const finished = job.status === 'completed' || job.status === 'failed';
  const outcome: RunOutcome = finished
    ? classifyRunOutcome({
        status: job.status,
        productsProcessed: job.result?.productsProcessed,
        productsFailed: job.result?.productsFailed,
        type: job.type,
        mode: job.mode,
      })
    : 'ok';
  const verdict = outcome === 'failed' ? 'Failed' : VERDICTS[job.status] || 'Pending';
  const isRunning = job.status === 'running';
  const startedAt = job.startedAt || job.createdAt;
  const processed = job.result?.productsProcessed ?? job.progress?.current ?? 0;
  const created = job.result?.productsCreated ?? 0;
  const updated = job.result?.productsUpdated ?? 0;
  const duration = isRunning
    ? `${formatDuration(startedAt)} elapsed`
    : formatDuration(job.startedAt, job.completedAt);
  const kind = JOB_TYPE_LABELS[job.type] || job.type;
  const { trigger, actor } = resolveTrigger(job);
  const catalog = options.catalogSize;

  let result: string;
  if (isRunning) {
    const total = job.progress?.total;
    result = total ? `${processed} of ~${total} processed · in progress` : `${processed} processed · in progress`;
  } else if (
    verdict === 'Failed' &&
    (Boolean(job.result?.sourceUnavailable) || /is down for maintenance/i.test(job.error || ''))
  ) {
    result = 'Stopped: source is down';
  } else if (verdict === 'Failed' && processed > 0 && (job.result?.productsFailed ?? 0) >= processed) {
    result = `${processed} of ${processed} checks failed`;
  } else if (verdict === 'Failed') {
    result = catalog && catalog > 0
      ? `${processed} of ${catalog} processed · aborted`
      : `${processed} processed · aborted`;
  } else if (verdict === 'Completed') {
    result = `${processed} processed · ${updated} updated`;
  } else if (verdict === 'Cancelled') {
    result = `${processed} processed · cancelled`;
  } else {
    result = 'Waiting to start';
  }

  return {
    id: job.id,
    verdict,
    kind,
    trigger,
    actor,
    processed,
    created,
    updated,
    duration,
    startedAt,
    startedLabel: formatRunTimestamp(startedAt),
    relative: formatRelativeTime(job.completedAt || startedAt),
    result,
    meta: [formatRunTimestamp(startedAt), kind, trigger, duration].filter(Boolean).join(' · '),
    logs: job.recentLogs || [],
    error: runError(job),
    outcome,
    ...(outcome === 'partial'
      ? { partialFailure: `${job.result?.productsFailed ?? 0} of ${processed} failed` }
      : {}),
    isRunning,
  };
}

/** Log line colors from the handoff: info, highlight, warn, error, success. */
export function logLineColor(log: IngestionJobLog): string {
  if (log.level === 'error') return ingest.color.dangerInk;
  if (log.level === 'warn') return ingest.color.warningInk;
  if (log.level === 'debug') return ingest.color.faintAlt;
  if (/\b(completed|finished|persisted)\b/i.test(log.message)) return ingest.color.successInk;
  if (/\b(in progress|processing|page \d)/i.test(log.message)) return ingest.color.accentHover;
  return ingest.color.muted;
}

export function formatLogLine(log: IngestionJobLog): string {
  const stamp = new Date(log.timestamp);
  const time = Number.isNaN(stamp.getTime()) ? '--:--:--' : stamp.toLocaleTimeString();
  return `[${time}] [${log.level.toUpperCase()}] ${log.message}`;
}

export interface ConnectorAlert {
  title: string;
  when: string;
  body: string;
  /** Run the "View log" button should open — always the run the body describes. */
  runId?: string;
}

/**
 * The alert body is derived from the last *finished* run record, never stored: a failed
 * run shows its real error and the count it reached, a zero-product run shows
 * that its rows never persisted, and a flapping connector shows its failure
 * rate. Three different fixes, three different messages.
 */
export function deriveConnectorAlert(
  brand: IngestionBrandInfo,
  /** Facts for `brand.lastRun` — the last *finished* run, never a newer retry. */
  lastFinished: RunFacts | null
): ConnectorAlert | null {
  const productCount = brand.stats?.productCount ?? 0;
  const recentFinished = (brand.recentRuns || []).filter(
    (run) => run.status === 'completed' || run.status === 'failed'
  );
  const recentFailures = recentFinished.filter((run) => classifyRunOutcome(run) !== 'ok').length;

  const lastRun = brand.lastRun;
  if (lastRun && classifyRunOutcome(lastRun) === 'failed') {
    const reason = lastFinished?.error || brand.lastError || 'the connector reported no error message';
    const reached = lastFinished
      ? ` · ${lastFinished.processed}${productCount > 0 ? ` of ${productCount}` : ''} products reached before abort`
      : '';
    return {
      title: `${lastFinished?.kind || 'Full scrape'} failed`,
      when: formatRelativeTime(lastRun.completedAt || lastRun.startedAt),
      body: `${reason}${reached}`,
      runId: lastFinished?.id || lastRun.id,
    };
  }

  if (brand.lastRun && productCount === 0) {
    const processed = lastFinished?.processed ?? brand.lastRun.productsProcessed ?? 0;
    return {
      title: 'Runs succeed but the catalog is empty',
      when: formatRelativeTime(brand.lastRun.completedAt || brand.lastRun.startedAt),
      body: processed > 0
        ? `${processed} products processed, 0 persisted · rows were rejected before they reached the catalog`
        : 'The last run returned 0 products · the source selectors likely no longer match',
      runId: lastFinished?.id || brand.lastRun.id,
    };
  }

  if (recentFailures >= 3) {
    return {
      title: 'Succeeding now, but failing often',
      when: formatRelativeTime(brand.lastRun?.completedAt || brand.lastRun?.startedAt),
      body: `${recentFailures} of the last ${recentFinished.length} runs failed · the connector is not stable`,
      runId: lastFinished?.id,
    };
  }

  return null;
}
